import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import LoginPage from './pages/LoginPage'
import ExecutiveDashboard from './pages/ExecutiveDashboard'
import AnalystView from './pages/AnalystView'
import DispatcherView from './pages/DispatcherView'
import ScadaView from './pages/ScadaView'
import RoutesPage from './pages/RoutesPage'
import MetersPage from './pages/MetersPage'
import UsersPage from './pages/UsersPage'
import OperatorPage from './pages/OperatorPage'
import SettingsPage from './pages/SettingsPage'
import AccountPage from './pages/AccountPage'
import ReportsPage from './pages/ReportsPage'
import TasksPage from './pages/TasksPage'
import ActivatePage from './pages/ActivatePage'
import OnboardPage from './pages/OnboardPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import AppShell from './components/AppShell'
import AdminLayout from './pages/admin/AdminLayout'
import AdminLoginPage from './pages/admin/AdminLoginPage'
import AdminOverview from './pages/admin/AdminOverview'
import AdminTenants from './pages/admin/AdminTenants'
import AdminTenantDetail from './pages/admin/AdminTenantDetail'
import AdminUsers from './pages/admin/AdminUsers'
import { useAuthStore } from './store/authStore'

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1 } },
})

function PrivateRoute({
  children,
  requiredRoles,
  requiredModules,
}: {
  children: React.ReactNode
  requiredRoles?: string[]
  requiredModules?: string[]
}) {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) return <Navigate to="/login" replace />

  if (requiredRoles && user && !requiredRoles.some((r) => user.roles.includes(r))) {
    return <Navigate to="/" replace />
  }

  // admin bypasses module checks; fall through if modules field is missing (old JWT)
  if (requiredModules && user && !user.roles.includes('admin') && (user.modules?.length ?? 0) > 0) {
    if (!requiredModules.some((m) => user.modules.includes(m))) {
      return <Navigate to="/" replace />
    }
  }

  return <AppShell>{children}</AppShell>
}

function AdminGate() {
  // Admin area is independent of tenant auth — uses its own Google-based session
  return <AdminLayout />
}

function landingPage(roles: string[], modules: string[]): string {
  const isAdmin = roles.includes('admin')
  // Mirrors PrivateRoute's own module gate exactly, including its "modules
  // array empty/missing => no gate" fallback for old JWTs — every branch
  // below now only returns a route the user can actually land on. Before
  // this, none of these branches checked modules at all, so a role match
  // whose tenant/account didn't have the assumed module enabled got sent
  // to a route that immediately bounced them back to "/", which picked
  // the same unreachable route again — an infinite redirect loop (blank
  // white screen, "Throttling navigation to prevent the browser from
  // hanging" in the console). Hit for real by a pump_operator whose
  // account only has the scada module, sent to /tasks (needs meter_reading
  // or repair) by the old unconditional roles.includes('pump_operator')
  // check below. pump_operator is now scada_operator/scada_repair
  // (docs/plan-scada-role-split.md) — same rights, split in two.
  const moduleGateActive = !isAdmin && modules.length > 0
  const canUse = (required: string[]) => !moduleGateActive || required.some((m) => modules.includes(m))

  if (roles.some((r) => ['admin', 'company_admin', 'executive'].includes(r))) return '/dashboard'
  if (roles.includes('dispatcher') && canUse(['meter_reading', 'repair'])) return '/dispatch'
  if (roles.some((r) => ['meter_reader', 'scada_operator', 'scada_repair', 'repair_crew'].includes(r)) && canUse(['meter_reading', 'repair'])) return '/tasks'
  if (roles.some((r) => ['scada_operator', 'scada_repair'].includes(r)) && canUse(['scada'])) return '/infrastruktura'
  if (roles.includes('analyst') && canUse(['meter_reading', 'scada'])) return '/analytics'
  if (canUse(['meter_reading'])) return '/meters'
  // Ungated fallback — /account requires only authentication, no
  // role/module check, so this can never itself be the unreachable route
  // that starts another loop.
  return '/account'
}

export default function App() {
  const { isAuthenticated, user } = useAuthStore()

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login"    element={<LoginPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/onboard/:token" element={<OnboardPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'executive']}>
                <ExecutiveDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/infrastruktura"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'executive', 'scada_operator', 'scada_repair', 'dispatcher']} requiredModules={['scada']}>
                <ScadaView />
              </PrivateRoute>
            }
          />
          <Route path="/scada" element={<Navigate to="/infrastruktura" replace />} />

          <Route
            path="/meters"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'executive', 'dispatcher', 'meter_reader', 'analyst']} requiredModules={['meter_reading']}>
                <MetersPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'analyst']} requiredModules={['meter_reading', 'scada']}>
                <AnalystView />
              </PrivateRoute>
            }
          />

          <Route
            path="/dispatch"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'dispatcher']} requiredModules={['meter_reading', 'repair']}>
                <DispatcherView />
              </PrivateRoute>
            }
          />

          <Route
            path="/rute"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'dispatcher']} requiredModules={['meter_reading']}>
                <RoutesPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/tasks"
            element={
              <PrivateRoute requiredRoles={['meter_reader', 'scada_operator', 'scada_repair', 'repair_crew', 'admin', 'company_admin', 'dispatcher']} requiredModules={['meter_reading', 'repair']}>
                <TasksPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin', 'analyst', 'executive']} requiredModules={['meter_reading', 'scada']}>
                <ReportsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/users"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin']}>
                <UsersPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/operator"
            element={
              <PrivateRoute requiredRoles={['admin']}>
                <OperatorPage />
              </PrivateRoute>
            }
          />

          {/* Global admin area — independent auth (Google), no AppShell */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminGate />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="tenants/:id" element={<AdminTenantDetail />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>

          <Route
            path="/settings"
            element={
              <PrivateRoute requiredRoles={['admin', 'company_admin']}>
                <SettingsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/account"
            element={
              <PrivateRoute>
                <AccountPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/"
            element={
              isAuthenticated
                ? <Navigate to={landingPage(user?.roles ?? [], user?.modules ?? [])} replace />
                : <Navigate to="/login" replace />
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
